/**
 * StaleBundleBanner — full-screen overlay shown when a stale-bundle
 * chunk-load failure is detected. Mounted once at the App root; sits
 * idle until `triggerStaleBundle()` fires the global event from a
 * dynamic-import catch block.
 *
 * The diagnosis is "Vercel served HTML where the previous bundle
 * expected a JS chunk because the deploy moved the hash". Refreshing
 * the tab loads the new index.html → new chunk hashes → import works
 * again. We pre-cache `location.reload()` rather than route via React
 * navigation so the page truly re-bootstraps from a fresh document.
 *
 * Web-only (the `triggerStaleBundle` helper no-ops on native), so we
 * gate the listener on Platform.OS without rendering anything on iOS /
 * Android. See `src/utils/staleBundle.ts` for the diagnostic logic.
 */
import React, { useEffect, useState } from 'react';
import { Modal, View, Text, TouchableOpacity, Platform, DeviceEventEmitter } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { STALE_BUNDLE_EVENT } from '../utils/staleBundle';
import { markReloadIntent } from '../utils/reloadIntent';

import { accentTextOn, ON_ACCENT_TEXT } from '../styles/accentText';
export const StaleBundleBanner: React.FC = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (Platform.OS !== 'web') return;
        const sub = DeviceEventEmitter.addListener(STALE_BUNDLE_EVENT, () => setVisible(true));
        return () => sub.remove();
    }, []);

    if (!visible || Platform.OS !== 'web') return null;

    const onReload = () => {
        try {
            if (typeof window !== 'undefined' && window.location) {
                // `true` argument is ignored by modern browsers, but
                // we want a hard navigation rather than a SPA push.
                // CORE-2091 — это перезагрузка, а не уход: кадр ухода из комнаты
                // на `pagehide` не нужен (см. utils/reloadIntent).
                markReloadIntent();
                window.location.reload();
            }
        } catch {
            // Reload doesn't normally throw, but if it does we still
            // want the user to be able to dismiss the banner manually.
            setVisible(false);
        }
    };

    return (
        <Modal visible transparent animationType="fade" onRequestClose={() => setVisible(false)}>
            <View
                accessibilityRole="alert"
                style={{
                    flex: 1,
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                }}
            >
                <View
                    style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: 20,
                        padding: 28,
                        maxWidth: 460,
                        width: '100%',
                        gap: 16,
                        ...(Platform.OS === 'web' && { boxShadow: '0 12px 40px rgba(0,0,0,0.18)' } as any),
                    } as any}
                >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                        <View
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                backgroundColor: 'rgba(232,93,4,0.10)',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Ionicons name="refresh-circle" size={28} color={accentTextOn('#E85D04')} />
                        </View>
                        <Text
                            style={{
                                fontSize: 18,
                                fontWeight: '700',
                                color: '#0F1419',
                                letterSpacing: -0.2,
                                flex: 1,
                            } as any}
                        >
                            Сайт обновился
                        </Text>
                    </View>

                    <Text style={{ fontSize: 14, lineHeight: 21, color: '#5C5652' } as any}>
                        Мы выкатили новую версию, пока вы были на этой странице.
                        {' '}Чтобы продолжить, обновите страницу — это займёт секунду.
                    </Text>

                    <TouchableOpacity
                        onPress={onReload}
                        activeOpacity={0.85}
                        accessibilityRole="button"
                        accessibilityLabel="Обновить страницу"
                        style={{
                            backgroundColor: '#E85D04',
                            paddingHorizontal: 20,
                            paddingVertical: 14,
                            borderRadius: 12,
                            alignItems: 'center',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            gap: 8,
                            ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                        } as any}
                    >
                        <Ionicons name="reload" size={18} color={ON_ACCENT_TEXT} />
                        <Text style={{ color: ON_ACCENT_TEXT, fontSize: 15, fontWeight: '700', letterSpacing: 0.2 } as any}>
                            Обновить страницу
                        </Text>
                    </TouchableOpacity>

                    {/* CORE-1192 — выход, которого не было. Раньше единственной
                        кнопкой была «Обновить», а `onRequestClose` был пуст: окно,
                        всплывшее не вовремя, убиралось только перезагрузкой — ровно
                        то, что владелец описал как «сообщение зависло на экране».
                        Закрытие безопасно: одноразовый флаг `triggered` в
                        staleBundle.ts уже взведён, так что до конца сессии окно не
                        вернётся, а страница продолжает работать на прежнем бандле —
                        сломается она только на чанке, которого больше нет на CDN. */}
                    <TouchableOpacity
                        onPress={() => setVisible(false)}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel="Обновить позже"
                        style={{
                            paddingHorizontal: 20,
                            paddingVertical: 10,
                            borderRadius: 12,
                            alignItems: 'center',
                            ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                        } as any}
                    >
                        <Text style={{ color: '#5C5652', fontSize: 14, fontWeight: '600' } as any}>
                            Позже
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};
